import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { buyAirtime, getUserWallet } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";
import { icons } from "../../utils/icons";
import BeneficiaryPicker from "../../components/feature/BeneficiaryPicker";
import CouponPicker    from "../../components/feature/CouponPicker";
import CashbackToggle      from "../../components/feature/CashbackToggle";
import PaymentConfirmSheet from "../../components/feature/PaymentConfirmSheet";           // ← NEW
import MarketingSlides from "../../components/feature/MarketingSlides";
import { PhoneRequiredGate } from "../../components/feature/ProfilePromptBanner";     // ← NEW
import { useBeneficiaries, saveBeneficiary } from "../../hooks/useBeneficiaries";
import { useAnalytics } from "../../hooks/useAnalytics";
import { detectNigerianNetwork } from "../../utils/detectNetwork";

// Network accent — mapped onto the semantic token scale so each dot/icon
// adapts correctly in dark mode instead of staying a fixed light-mode hex.
const NETWORKS = [
  { id: "MTN",     label: "MTN",     img: icons.mtn,    tone: "warning" },
  { id: "AIRTEL",  label: "Airtel",  img: icons.airtel, tone: "danger"  },
  { id: "GLO",     label: "Glo",     img: icons.glo,    tone: "success" },
  { id: "9MOBILE", label: "9mobile", img: null,          tone: "success" },
];
const QUICK_AMOUNTS = [100, 200, 500, 1000, 2000, 5000];

export default function AirtimePage() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userLoading = false, userReady }       = useAuth();
  const { mostRecent } = useBeneficiaries("airtime");

  const { track, conversion } = useAnalytics();

  const [network,  setNetwork]  = useState(searchParams.get("network") || mostRecent?.network || "MTN");
  const [phone,    setPhone]    = useState(mostRecent?.phone || "");
  const [amount,   setAmount]   = useState("");
  const [loading,  setLoading]  = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pinError,    setPinError]    = useState("");
  const [nairaBalance,  setNairaBalance]  = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [networkManuallySet, setNetworkManuallySet] = useState(
  Boolean(searchParams.get("network") || mostRecent?.network));


  // ── Coupon + cashback state ────────────────────────────────
  const [appliedCoupon,  setAppliedCoupon]  = useState(null);
  const [cashbackToUse,  setCashbackToUse]  = useState(0);

  // ── 🔍 AUTO-DETECT NETWORK EFFECT ──
  useEffect(() => {
  if (networkManuallySet) return;
  const detected = detectNigerianNetwork(phone);
  if (detected) setNetwork(detected);
}, [phone, networkManuallySet]);

  useEffect(() => {
  track.viewItem("airtime");
}, []);

  const rawAmount  = Number(amount) || 0;
  const couponSaving  = appliedCoupon?.discount || 0;
  const totalSavings  = couponSaving + cashbackToUse;
  const netAmount     = Math.max(rawAmount - totalSavings, 0);  // what user actually pays

  const isValid    = phone.length === 11 && rawAmount >= 50;
  const hasBalance = nairaBalance !== null && nairaBalance >= netAmount;
  const canPay     = isValid && hasBalance;

  useEffect(() => {
    getUserWallet()
      .then(r => { if (r.status) setNairaBalance(r.data?.nairaBalance || 0); })
      .catch(() => {})
      .finally(() => setBalanceLoading(false));
  }, []);

  // Clear coupon + cashback if amount drops
  useEffect(() => {
    if (rawAmount < 50) {
      setAppliedCoupon(null);
      setCashbackToUse(0);
    }
  }, [rawAmount]);

  const handleBeneficiarySelect = useCallback((b) => {
  if (b.network) {
    setNetwork(b.network);
    setNetworkManuallySet(true);   // ← added
  }
}, []);

const handlePay = () => {
  if (canPay && !loading) {
    track.beginCheckout("airtime", rawAmount); // fire before showing sheet
    setPinError("");
    setShowConfirm(true);
  }
};

  const executePay = async (pin) => {
    setShowConfirm(false);
    setLoading(true);
    try {
      const res = await buyAirtime(
        network, phone, rawAmount, user?.phone,
        appliedCoupon?.couponId || null,
        appliedCoupon?.discount || 0,
        cashbackToUse,
        pin,                        // ← cashback spend
      );
      if (res.status) {
        saveBeneficiary({ id: phone, type: "airtime", phone, network, label: phone, lastUsed: Date.now() });
        track.purchase("airtime", netAmount, res.transactionId);
        // If API tells you it's first ever bill payment:
        if (res.isFirstBillPayment) {
          conversion.firstPurchase(netAmount, "airtime");
        }
        navigate("/bills/success", {
          state: {
            type:       "airtime",
            title:      `${network} Airtime`,
            detail:     phone,
            amount:     netAmount,           // show what was charged
            grossAmount: rawAmount,
            discount: couponSaving,
            couponCode: appliedCoupon?.code || null,
            requestId:  res.requestId,
            txStatus:      res.txStatus || "completed",   // ← ADD THIS
            transactionId: res.transactionId,
          },
        });
      } else {
        toast.error(res.message || "Airtime purchase failed");
      }
    } catch (err) {
      const errData = err?.response?.data;
      const errCode = errData?.code || "";
      const errMsg  = errData?.message || "";

      if (errCode === "PIN_INVALID" || errCode === "PIN_LOCKED" || errCode === "PIN_REQUIRED") {
        setShowConfirm(true);
        setPinError(errMsg);
        setLoading(false);
        return;
      }

      // 202 PROCESSING — backend couldn't confirm with provider but
      // the order may be processing. Navigate to BillSuccessPage
      // with processing status so it polls for resolution.
      if (err?.response?.status === 202 || errCode === "PROCESSING") {
        navigate("/bills/success", {
          state: {
            type:       "airtime",
            title:      `${network} Airtime`,
            detail:     phone,
            amount:     netAmount,
            grossAmount: rawAmount,
            txStatus:   "processing",
            reference:  errData?.reference || null,
          },
        });
        return;
      }

      toast.error(errMsg || "Airtime purchase failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/bills")} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div>
              <h1 className="text-primary font-bold text-lg font-display">Buy Airtime</h1>
              <p className="text-muted text-xs">Recharge any network instantly</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-brand-light border border-brand-border px-3 py-1.5 rounded-xl">
            <i className="ri-wallet-3-line text-brand text-xs" />
            <span className="text-brand-text text-xs font-bold">
              {balanceLoading ? "…" : `₦${(nairaBalance || 0).toLocaleString()}`}
            </span>
          </div>
        </div>
      </div>

      <PhoneRequiredGate user={user} userLoading={userLoading} userReady={userReady}>
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-36 space-y-5">

        {/* ── Marketing slide for airtime page ─────────────── */}
        {/* compact=true → 90px height; shows slides tagged
            pages:["airtime"] AND pages:["home"]. Falls back to
            nothing if no slides are configured for this page. */}
        <MarketingSlides page="airtime" compact />

        {/* Network */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">
            Select Network
            {!networkManuallySet && phone.length >= 4 && (
              <span className="ml-2 text-xs font-normal text-muted">(detected from number)</span>
            )}
            </p>
          <div className="grid grid-cols-4 gap-2">
            {NETWORKS.map(net => (
              <button 
              key={net.id} 
              onClick={() => {
                setNetwork(net.id);
                setNetworkManuallySet(true);
              }}
                className={`flex flex-col items-center gap-2 py-3 rounded-2xl border-2 transition-all ${
                  network === net.id ? "border-brand bg-brand-light" : "border-border bg-surface"
                }`}>
                <div className="w-9 h-9 rounded-xl overflow-hidden flex items-center justify-center bg-surface-muted">
                  {net.img
                    ? <img src={net.img} className="w-7 h-7 object-contain" alt={net.label} />
                    : <i className="ri-phone-line text-lg" style={{ color: `var(--color-${net.tone})` }} />}
                </div>
                <span className="text-xs font-semibold text-secondary">{net.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Phone with BeneficiaryPicker */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">Phone Number</p>
          <BeneficiaryPicker
            type="airtime"
            value={phone}
            onChange={setPhone}
            onSelect={handleBeneficiarySelect}
            placeholder="0801 234 5678"
            numeric
            maxLength={11}
            inputMode="tel"
            suffix={phone.length === 11 ? <i className="ri-checkbox-circle-fill text-brand text-lg" /> : null}
          />
        </div>

        {/* Amount */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">Amount</p>
          <div className="bg-surface border-2 border-border-strong rounded-2xl flex items-center px-4 focus-within:border-brand transition-colors mb-3">
            <span className="text-muted font-bold mr-2">₦</span>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              className="flex-1 py-4 outline-none text-primary text-sm bg-transparent placeholder:text-faint" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {QUICK_AMOUNTS.map(amt => (
              <button key={amt} onClick={() => setAmount(String(amt))}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  amount === String(amt) ? "bg-brand text-on-brand border-brand" : "bg-surface text-secondary border-border-strong hover:border-brand-border"
                }`}>
                ₦{amt >= 1000 ? `${amt / 1000}k` : amt}
              </button>
            ))}
          </div>
        </div>

        {/* ── Cashback toggle ── */}
        {rawAmount >= 50 && (
          <CashbackToggle
            purchaseAmount={rawAmount}
            appliedAmount={cashbackToUse}
            onApply={setCashbackToUse}
          />
        )}

        {/* ── Coupon picker ── */}
        {/* CouponPicker fetches available coupons for "airtime"
            with the current amount. User can pick from the list
            or type a code manually. Once applied, shows the
            green "applied" state with a Remove button. */}
        {rawAmount >= 50 && (
          <div>
            <p className="text-secondary text-sm font-semibold mb-3">Coupon / Discount</p>
            <CouponPicker
              service="airtime"
              amount={rawAmount}
              appliedCoupon={appliedCoupon}
              onApply={setAppliedCoupon}
              onRemove={() => setAppliedCoupon(null)}
            />
          </div>
        )}

        {/* Order summary — shown when any saving is applied */}
        {(appliedCoupon || cashbackToUse > 0) && rawAmount > 0 && (
          <div className="bg-brand-light border border-brand-border rounded-2xl px-4 py-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted">Airtime amount</span>
              <span className="text-primary font-semibold">₦{rawAmount.toLocaleString()}</span>
            </div>
            {cashbackToUse > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-brand-text font-semibold flex items-center gap-1">
                  <i className="ri-hand-coin-line text-xs" />Cashback
                </span>
                <span className="text-brand-text font-bold">−₦{cashbackToUse.toLocaleString()}</span>
              </div>
            )}
            {appliedCoupon && (
              <div className="flex justify-between text-sm">
                <span className="text-brand-text font-semibold flex items-center gap-1">
                  <i className="ri-coupon-3-line text-xs" />{appliedCoupon.code}
                </span>
                <span className="text-brand-text font-bold">−₦{couponSaving.toLocaleString()}</span>
              </div>
            )}
            <div className="h-px bg-brand-border" />
            <div className="flex justify-between text-sm">
              <span className="text-primary font-bold">You pay</span>
              <span className="text-brand-text font-bold text-base">₦{netAmount.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>

      </PhoneRequiredGate>
      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border px-5 py-4 space-y-2">
        {isValid && !hasBalance && nairaBalance !== null && (
          <div className="flex items-center gap-2 px-3 py-2 bg-danger-light border border-danger-border rounded-xl">
            <i className="ri-error-warning-line text-danger text-sm" />
            <p className="text-danger-text text-xs font-medium flex-1">
              Insufficient balance. <button onClick={() => navigate("/deposit")} className="underline font-semibold">Top up</button>
            </p>
          </div>
        )}
        <button onClick={handlePay} disabled={!canPay || loading}
          className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
          {loading
            ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing…</>
            : !isValid ? "Enter phone number and amount"
            : !hasBalance ? "Insufficient balance"
            : totalSavings > 0
              ? `Pay ₦${netAmount.toLocaleString()} (saved ₦${totalSavings.toLocaleString()})`
              : `Pay ₦${rawAmount.toLocaleString()}`}
        </button>
      </div>
      <PaymentConfirmSheet
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={executePay}
        loading={loading}
        pinError={pinError}
        amount={rawAmount}
        netAmount={netAmount}
        couponSaving={couponSaving}
        cashbackUsed={cashbackToUse}
        rows={[
          { label: "Network", value: NETWORKS.find(n => n.id === network)?.label || network, critical: true },
          { label: "Phone",   value: phone, mono: true, critical: true },
        ]}
        confirmLabel="Buy Airtime"
      />
    </div>
  );
}
